import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, MapPin, Headphones, Image, CheckCircle, AlertCircle, Info, Trash2 } from 'lucide-react';
import { TourPoint } from '../../types';
import Button from '../UI/Button';
import apiService from '../../services/api';
import toast from 'react-hot-toast';
import { languages } from '../../constants/Languages';

interface TourStopCreationFormProps {
  tourId: number;
  onClose: () => void;
  onSuccess?: (tourStop: TourPoint) => void;
  initialData?: Partial<TourPoint>;
  isEditing?: boolean;
}

interface TourStopFormData {
  name: string;
  discription: string;
  lattitude: string;
  longitude: string;
  radius: number;
  thumbnailUrl: string;
  mainAudioUrl: string;
  audioUrl: string;
  isHidePreview: boolean;
  isHideStop: boolean;
  status: string;
  stopLanguagesMap: string;
  stopLanguagesPreviewMap: string;
}

interface TourStopFiles {
  thumbnail?: File;
  mainAudio?: File;
  previewAudio?: File;
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

const TourStopCreationForm: React.FC<TourStopCreationFormProps> = ({
  tourId,
  onClose,
  onSuccess,
  initialData,
  isEditing = false,
}) => {
  const [formData, setFormData] = useState<TourStopFormData>({
    name: '',
    discription: '',
    lattitude: '',
    longitude: '',
    radius: 50,
    thumbnailUrl: '',
    mainAudioUrl: '',
    audioUrl: '',
    isHidePreview: false,
    isHideStop: false,
    status: 'active',
    stopLanguagesMap: '[]',
    stopLanguagesPreviewMap: '[]',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [files, setFiles] = useState<TourStopFiles>({});
  const [existingFiles, setExistingFiles] = useState<{
    thumbnail?: { url: string; name: string };
    mainAudio?: { url: string; name: string };
    previewAudio?: { url: string; name: string };
  }>({});

  const [stopName, setStopName] = useState<Array<{ language: string; text: string }>>([{ language: '', text: '' }]);
  const [stopDescription, setStopDescription] = useState<Array<{ language: string; text: string }>>([{ language: '', text: '' }]);
  const [mainAudios, setMainAudios] = useState<Array<{ language: string; file?: File; existingUrl?: string; existingName?: string }>>([{ language: '' }]);
  const [previewAudios, setPreviewAudios] = useState<Array<{ language: string; file?: File; existingUrl?: string; existingName?: string }>>([{ language: '' }]);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const previousAvailableLanguagesRef = useRef<string[]>([]);
  
  // Confirmation dialog state
  const [confirmationDialog, setConfirmationDialog] = useState<{
    show: boolean;
    message: string;
    onConfirm: () => void;
  }>({
    show: false,
    message: '',
    onConfirm: () => {},
  });

  useEffect(() => {
    const fetchTourLanguages = async () => {
      try {
        const tour = await apiService.getTourById(tourId);
        // Extract languages from tour name field
        let tourLanguages: string[] = [];
        
        if (tour.name) {
          try {
            const nameParsed = typeof tour.name === 'string' ? JSON.parse(tour.name) : tour.name;
            if (Array.isArray(nameParsed) && nameParsed.length > 0) {
              tourLanguages = nameParsed
                .map((item: any) => item.language)
                .filter((lang: string) => lang && lang.trim() !== '');
            }
          } catch (error) {
            // ignore tour name parse failure
          }
        }
        
        // If no languages found, allow all languages as fallback
        if (tourLanguages.length > 0) {
          setAvailableLanguages(tourLanguages);
          
          if (!isEditing && !initialData && tourLanguages.length > 0) {
            setStopName(prev => {
              // Only pre-populate if still in default state
              if (prev.length === 1 && !prev[0].language && !prev[0].text) {
                return tourLanguages.map(lang => ({ language: lang, text: '' }));
              }
              return prev;
            });
            setStopDescription(prev => {
              if (prev.length === 1 && !prev[0].language && !prev[0].text) {
                return tourLanguages.map(lang => ({ language: lang, text: '' }));
              }
              return prev;
            });
            setMainAudios(prev => {
              if (prev.length === 1 && !prev[0].language) {
                return tourLanguages.map(lang => ({ language: lang }));
              }
              return prev;
            });
            setPreviewAudios(prev => {
              if (prev.length === 1 && !prev[0].language) {
                return tourLanguages.map(lang => ({ language: lang }));
              }
              return prev;
            });
          }
        } else {
          const allLanguageCodes = languages.map((lang: any) => lang.code);
          setAvailableLanguages(allLanguageCodes);
          
          if (!isEditing && !initialData && allLanguageCodes.length > 0) {
            setStopName(prev => {
              if (prev.length === 1 && !prev[0].language && !prev[0].text) {
                return allLanguageCodes.map(lang => ({ language: lang, text: '' }));
              }
              return prev;
            });
            setStopDescription(prev => {
              if (prev.length === 1 && !prev[0].language && !prev[0].text) {
                return allLanguageCodes.map(lang => ({ language: lang, text: '' }));
              }
              return prev;
            });
            setMainAudios(prev => {
              if (prev.length === 1 && !prev[0].language) {
                return allLanguageCodes.map(lang => ({ language: lang }));
              }
              return prev;
            });
            setPreviewAudios(prev => {
              if (prev.length === 1 && !prev[0].language) {
                return allLanguageCodes.map(lang => ({ language: lang }));
              }
              return prev;
            });
          }
        }
      } catch (error) {
        // Fallback: allow all languages on error
        const allLanguageCodes = languages.map((lang: any) => lang.code);
        setAvailableLanguages(allLanguageCodes);
        
        // Pre-populate with all languages for new tour stops on error
        if (!isEditing && !initialData && allLanguageCodes.length > 0) {
          setStopName(prev => {
            if (prev.length === 1 && !prev[0].language && !prev[0].text) {
              return allLanguageCodes.map(lang => ({ language: lang, text: '' }));
            }
            return prev;
          });
          setStopDescription(prev => {
            if (prev.length === 1 && !prev[0].language && !prev[0].text) {
              return allLanguageCodes.map(lang => ({ language: lang, text: '' }));
            }
            return prev;
          });
          setMainAudios(prev => {
            if (prev.length === 1 && !prev[0].language) {
              return allLanguageCodes.map(lang => ({ language: lang }));
            }
            return prev;
          });
          setPreviewAudios(prev => {
            if (prev.length === 1 && !prev[0].language) {
              return allLanguageCodes.map(lang => ({ language: lang }));
            }
            return prev;
          });
        }
      }
    };

    fetchTourLanguages();
  }, [tourId, isEditing, initialData]);

  useEffect(() => {
    // Reset validation state when form opens or initialData changes
    setHasAttemptedSubmit(false);
    
    // Only process initialData if it exists (editing mode)
    // For new stops (no initialData), let the first useEffect handle language pre-population
    // This prevents this useEffect from interfering with language loading for new stops
    if (!initialData) {
      // When creating a new stop, don't do anything here
      // The first useEffect (fetchTourLanguages) will handle pre-populating languages
      return;
    }
    
    // Reset ref when initialData changes (editing different stop)
    previousAvailableLanguagesRef.current = [];
    
    // Process initialData for editing mode
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        discription: initialData.discription || '',
        lattitude: initialData.lattitude?.toString() || '',
        longitude: initialData.longitude?.toString() || '',
        radius: initialData.radius || 50,
        thumbnailUrl: initialData.thumbnailUrl || '',
        mainAudioUrl: initialData.mainAudioUrl || '',
        audioUrl: initialData.audioUrl || '',
        isHidePreview: initialData.isHidePreview === 'true',
        isHideStop: initialData.isHideStop === 'true',
        status: initialData.status || 'active',
        stopLanguagesMap: initialData.stopLanguagesMap || '[]',
        stopLanguagesPreviewMap: initialData.stopLanguagesPreviewMap || '[]',
      });

      // Parse name entries first (this is the source of truth for languages)
      let nameEntries: Array<{ language: string; text: string }> = [];
      if (initialData.name) {
        try {
          const nameParsed = typeof initialData.name === 'string' ? JSON.parse(initialData.name) : initialData.name;
          if (Array.isArray(nameParsed) && nameParsed.length > 0) {
            nameEntries = nameParsed;
          } else {
            const fallbackName = [{ language: 'en', text: typeof initialData.name === 'string' ? initialData.name : JSON.stringify(initialData.name) }];
            nameEntries = fallbackName;
          }
        } catch {
          const fallbackName = [{ language: 'en', text: initialData.name }];
          nameEntries = fallbackName;
        }
      }

      // Merge with availableLanguages: Add any tour languages that aren't in existing stop data
      // and remove languages that are no longer in the tour
      // This ensures that when a new language is added to the tour, it appears in existing stops
      // and when a language is deleted from the tour, it's removed from existing stops
      // Only apply filtering when availableLanguages has been loaded (changed from empty to non-empty)
      // This prevents the race condition where languages appear then disappear
      const wasEmpty = previousAvailableLanguagesRef.current.length === 0;
      const isNowLoaded = availableLanguages.length > 0;
      const languagesJustLoaded = wasEmpty && isNowLoaded;
      
      if (isEditing && availableLanguages.length > 0) {
        // Only apply filtering/merging if languages just loaded OR if we're updating after initial load
        // This prevents filtering on the very first render when availableLanguages is empty
        if (languagesJustLoaded || previousAvailableLanguagesRef.current.length > 0) {
          const existingLanguages = nameEntries.map(item => item.language);
          
          // Remove languages that are no longer in availableLanguages (deleted from tour)
          nameEntries = nameEntries.filter(item => availableLanguages.includes(item.language));
          
          // Add missing languages to nameEntries with empty text (newly added to tour)
          const missingLanguages = availableLanguages.filter(lang => !existingLanguages.includes(lang));
          missingLanguages.forEach(lang => {
            nameEntries.push({ language: lang, text: '' });
          });
        }
      }
      
      // Update ref to track previous availableLanguages value
      previousAvailableLanguagesRef.current = [...availableLanguages];

      // Parse description entries
      let descEntries: Array<{ language: string; text: string }> = [];
      if (initialData.discription) {
        try {
          const descParsed = typeof initialData.discription === 'string' ? JSON.parse(initialData.discription) : initialData.discription;
          if (Array.isArray(descParsed) && descParsed.length > 0) {
            descEntries = descParsed;
          }
        } catch {
          // descEntries remains empty
        }
      }

      // Parse mainAudioUrl
      let parsedMainAudioUrl: Array<{ language: string; url: string }> = [];
      if (initialData.mainAudioUrl) {
        try {
          const parsed = typeof initialData.mainAudioUrl === 'string' ? JSON.parse(initialData.mainAudioUrl) : initialData.mainAudioUrl;
          if (Array.isArray(parsed)) {
            parsedMainAudioUrl = parsed;
          }
        } catch (error) {
          // ignore parse failure
        }
      }

      // Parse stopLanguagesMap
      let parsedStopLanguagesMap: Array<{ language: string; file: string }> = [];
      if (initialData.stopLanguagesMap) {
        try {
          const parsed = typeof initialData.stopLanguagesMap === 'string' ? JSON.parse(initialData.stopLanguagesMap) : initialData.stopLanguagesMap;
          if (Array.isArray(parsed)) {
            parsedStopLanguagesMap = parsed;
          }
        } catch {
          // parsedStopLanguagesMap remains empty
        }
      }

      // Parse stopLanguagesPreviewMap
      let parsedPreviewMap: Array<{ language: string; file: string }> = [];
      if (initialData.stopLanguagesPreviewMap) {
        try {
          const parsed = typeof initialData.stopLanguagesPreviewMap === 'string' ? JSON.parse(initialData.stopLanguagesPreviewMap) : initialData.stopLanguagesPreviewMap;
          if (Array.isArray(parsed)) {
            parsedPreviewMap = parsed;
          }
        } catch {
          // parsedPreviewMap remains empty
        }
      }

      // Now set all the state with merged languages
      setStopName(nameEntries.length > 0 ? nameEntries : [{ language: '', text: '' }]);
      
      // Sync description entries with name entries (ensure all languages have descriptions)
      const syncedDescEntries = nameEntries.map((nameItem: any) => {
        const descItem = descEntries.find((desc: any) => desc.language === nameItem.language);
        return {
          language: nameItem.language,
          text: descItem?.text || ''
        };
      });
      setStopDescription(syncedDescEntries.length > 0 ? syncedDescEntries : [{ language: '', text: '' }]);

      // Sync main audio entries with name languages
      const audioEntries = nameEntries.map((nameItem: any) => {
        // Try mainAudioUrl first
        const audioItem = parsedMainAudioUrl.find((audio: any) => audio.language === nameItem.language);
        if (audioItem) {
          return {
            language: nameItem.language || '',
            existingUrl: audioItem.url || '',
            existingName: audioItem.url ? audioItem.url.split('/').pop() : undefined,
          };
        }
        // Fallback to stopLanguagesMap
        const mapItem = parsedStopLanguagesMap.find((item: any) => item.language === nameItem.language);
        if (mapItem) {
          return {
            language: nameItem.language || '',
            existingUrl: mapItem.file || '',
            existingName: mapItem.file ? mapItem.file.split('/').pop() : undefined,
          };
        }
        // New language - no existing audio
        return {
          language: nameItem.language || '',
        };
      });
      setMainAudios(audioEntries.length > 0 ? audioEntries : [{ language: '' }]);

      // Sync preview audio entries with name languages
      const previewAudioEntries = nameEntries.map((nameItem: any) => {
        const mapItem = parsedPreviewMap.find((item: any) => item.language === nameItem.language);
        return {
          language: nameItem.language || '',
          existingUrl: mapItem?.file || '',
          existingName: mapItem?.file ? mapItem.file.split('/').pop() : undefined,
        };
      });
      setPreviewAudios(previewAudioEntries.length > 0 ? previewAudioEntries : [{ language: '' }]);

      if (initialData.thumbnailUrl) {
        setExistingFiles(prev => ({
          ...prev,
          thumbnail: {
            url: initialData.thumbnailUrl!,
            name: initialData.thumbnailUrl!.split('/').pop() || 'thumbnail'
          }
        }));
      }
    }
    // Note: When creating a new stop (no initialData), this useEffect returns early
    // The first useEffect (fetchTourLanguages) handles pre-populating languages from the tour
  }, [initialData, availableLanguages, isEditing]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: checked,
        };
        
        // If "Hide from tour stops" is checked, automatically check and lock "Hide from preview"
        if (name === 'isHideStop' && checked) {
          newData.isHidePreview = true;
        }
        
        return newData;
      });
    } else if (name === 'radius') {
      // Handle radius as number
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? 0 : parseInt(value, 10),
      }));
    } else {
      // Handle all other fields as strings
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };



  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files: fileList } = e.target;
    if (fileList && fileList[0]) {
      setFiles(prev => ({
        ...prev,
        [name]: fileList[0],
      }));
      
      // Clear existing file when new file is selected
      setExistingFiles(prev => {
        const newState = { ...prev };
        if (name === 'thumbnail') {
          newState.thumbnail = undefined;
        } else if (name === 'mainAudio') {
          newState.mainAudio = undefined;
        } else if (name === 'previewAudio') {
          newState.previewAudio = undefined;
        }
        return newState;
      });
      
      // Update form data with file name for display
      setFormData(prev => ({
        ...prev,
        [name === 'thumbnail' ? 'thumbnailUrl' : name === 'mainAudio' ? 'mainAudioUrl' : 'audioUrl']: fileList[0].name,
      }));
    }
  };

  const removeFile = (fileType: keyof TourStopFiles) => {
    setFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[fileType];
      return newFiles;
    });
    
    // Clear the corresponding URL field
    setFormData(prev => ({
      ...prev,
      [fileType === 'thumbnail' ? 'thumbnailUrl' : fileType === 'mainAudio' ? 'mainAudioUrl' : 'audioUrl']: '',
    }));
  };

  const handleRemoveFileClick = (fileType: keyof TourStopFiles) => {
    const fileTypeName = fileType === 'thumbnail' ? 'thumbnail image' : fileType === 'mainAudio' ? 'main audio' : 'preview audio';
    setConfirmationDialog({
      show: true,
      message: `Are you sure you want to remove the ${fileTypeName}?`,
      onConfirm: () => {
        setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
        removeFile(fileType);
      },
    });
  };

  const removeExistingFile = (fileType: 'thumbnail' | 'mainAudio' | 'previewAudio') => {
    setExistingFiles(prev => {
      const newState = { ...prev };
      if (fileType === 'thumbnail') {
        newState.thumbnail = undefined;
      }
      return newState;
    });
    
    setFormData(prev => ({
      ...prev,
      [fileType === 'thumbnail' ? 'thumbnailUrl' : '']: '',
    }));
  };

  const handleRemoveExistingFileClick = (fileType: 'thumbnail' | 'mainAudio' | 'previewAudio') => {
    const fileTypeName = fileType === 'thumbnail' ? 'thumbnail image' : fileType === 'mainAudio' ? 'main audio' : 'preview audio';
    setConfirmationDialog({
      show: true,
      message: `Are you sure you want to remove the existing ${fileTypeName}?`,
      onConfirm: () => {
        setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
        removeExistingFile(fileType);
      },
    });
  };



  const handleSelectLanguage = (index: number, language: string) => {
    setStopName(stopName.map((item, i) => i === index ? { ...item, language } : item));
    setStopDescription(stopDescription.map((item, i) => i === index ? { ...item, language } : item));
    setMainAudios(mainAudios.map((item, i) => i === index ? { ...item, language } : item));
    setPreviewAudios(previewAudios.map((item, i) => i === index ? { ...item, language } : item));
  };

  const handleAudioFileChange = (index: number, audioType: 'main' | 'preview', file: File | null) => {
    if (audioType === 'main') {
      setMainAudios(mainAudios.map((item, i) => 
        i === index ? { ...item, file: file || undefined, existingUrl: file ? undefined : item.existingUrl, existingName: file ? undefined : item.existingName } : item
      ));
    } else {
      setPreviewAudios(previewAudios.map((item, i) => 
        i === index ? { ...item, file: file || undefined, existingUrl: file ? undefined : item.existingUrl, existingName: file ? undefined : item.existingName } : item
      ));
    }
  };

  const handleRemoveAudioFile = (index: number, audioType: 'main' | 'preview') => {
    if (audioType === 'main') {
      setMainAudios(mainAudios.map((item, i) => 
        i === index ? { language: item.language } : item
      ));
    } else {
      setPreviewAudios(previewAudios.map((item, i) => 
        i === index ? { language: item.language } : item
      ));
    }
  };

  const handleRemoveAudioFileClick = (index: number, audioType: 'main' | 'preview') => {
    const audioTypeName = audioType === 'main' ? 'main audio' : 'preview audio';
    const audioArray = audioType === 'main' ? mainAudios : previewAudios;
    const language = audioArray[index]?.language || '';
    const languageName = languages.find((l: any) => l.code === language)?.name || language || 'this language';
    
    setConfirmationDialog({
      show: true,
      message: `Are you sure you want to remove the ${audioTypeName} for ${languageName}?`,
      onConfirm: () => {
        setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
        handleRemoveAudioFile(index, audioType);
      },
    });
  };



  const validateForm = async (): Promise<boolean> => {
    try {
      const validationData = {
        ...formData,
        tourId,
        isHidePreview: formData.isHidePreview ? 'true' : 'false',
        isHideStop: formData.isHideStop ? 'true' : 'false',
      };

      const result = await apiService.validateTourStopData(validationData);
      setValidation(result);
      setShowValidation(true);

      if (!result.isValid) {
        toast.error('Please fix the validation errors before proceeding');
        return false;
      }

      if (result.warnings.length > 0) {
        toast('Tour stop created with warnings. Please review them.', {
          icon: '⚠️',
          duration: 5000,
        });
      }

      return true;
    } catch (error) {
      toast.error('Validation failed. Please check your data.');
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Mark that user has attempted to submit
    setHasAttemptedSubmit(true);
    
    // Collect all validation errors
    const errors: string[] = [];
    
    // First, validate all language fields (name, description, main audio) for all languages
    // Get all languages that have a language selected
    const languagesWithSelection = stopName
      .map((item, index) => ({
        index,
        language: item.language || stopDescription[index]?.language || mainAudios[index]?.language || '',
        nameEntry: item,
        descEntry: stopDescription[index],
        audioEntry: mainAudios[index]
      }))
      .filter(item => item.language && item.language.trim() !== '');

    if (languagesWithSelection.length === 0) {
      errors.push('Please select at least one language');
    }

    // Check for missing names
    const missingNameLanguages: string[] = [];
    const missingDescLanguages: string[] = [];
    const missingAudioLanguages: string[] = [];

    languagesWithSelection.forEach(({ language, nameEntry, descEntry, audioEntry }) => {
      const langName = languages.find(l => l.code === language)?.name || language;
      
      if (!nameEntry?.text || nameEntry.text.trim() === '') {
        missingNameLanguages.push(langName);
      }
      
      if (!descEntry?.text || descEntry.text.trim() === '') {
        missingDescLanguages.push(langName);
      }
      
      const hasMainAudio = audioEntry?.language && (audioEntry.file || audioEntry.existingUrl);
      if (!hasMainAudio) {
        missingAudioLanguages.push(langName);
      }
    });

    // Add language field errors
    if (missingNameLanguages.length > 0) {
      errors.push(`Name is required for all languages. Please provide name for: ${missingNameLanguages.join(', ')}`);
    }

    if (missingDescLanguages.length > 0) {
      errors.push(`Description is required for all languages. Please provide description for: ${missingDescLanguages.join(', ')}`);
    }

    if (missingAudioLanguages.length > 0) {
      errors.push(`Main audio is required for all languages. Please provide main audio for: ${missingAudioLanguages.join(', ')}`);
    }

    // Check location fields
    if (!formData.lattitude.trim() || !formData.longitude.trim()) {
      errors.push('Latitude and longitude are required');
    }
    
    if (!formData.radius || formData.radius <= 0) {
      errors.push('Radius is required and must be greater than 0');
    }

    // Check thumbnail
    if (!files.thumbnail && !existingFiles.thumbnail) {
      errors.push('Thumbnail image is required. Please select a new image or keep the existing one.');
    }

    // Display errors one by one sequentially
    if (errors.length > 0) {
      // Show errors sequentially, one after another
      const toastDuration = 4000; // 4 seconds per toast
      const delayBetweenToasts = 500; // 500ms gap between toasts
      const totalDelayPerToast = toastDuration + delayBetweenToasts;
      
      errors.forEach((error, index) => {
        setTimeout(() => {
          toast.error(error, {
            duration: toastDuration,
            position: 'top-right',
          });
        }, index * totalDelayPerToast);
      });
      return;
    }

    // Get valid entries for submission
    const validNameEntries = stopName.filter(item => item.text && item.language);
    const validDescEntries = stopDescription.filter(item => item.text && item.language);
    const validMainAudios = mainAudios.filter(item => item.language && (item.file || item.existingUrl));

    setIsLoading(true);

    try {
      let tourStop: TourPoint;

      const nameArray = validNameEntries.map(item => ({ text: item.text, language: item.language }));
      const descriptionArray = validDescEntries.map(item => ({ text: item.text, language: item.language }));
      
      // Helper function to extract base URL path
      const getBasePath = () => {
        if (initialData?.thumbnailUrl) {
          // Extract base path from thumbnail URL
          // e.g., https://lon1.digitaloceanspaces.com/trippy-tour-apps/com.trippyflavourapp.scotland/tour/44/tour-points/98/...
          const urlParts = initialData.thumbnailUrl.split('/tour-points');
          if (urlParts.length > 0) {
            return urlParts[0];
          }
        }
        // Fallback: construct base path
        return `https://lon1.digitaloceanspaces.com/trippy-tour-apps/com.trippyflavourapp.scotland/tour/${tourId}`;
      };
      
      const mainAudioUrlArray = validNameEntries.map(item => {
        const audioEntry = validMainAudios.find(audio => audio.language === item.language);
        let url = '';

        if (audioEntry?.existingUrl) {
          // Use existing URL if available
          url = audioEntry.existingUrl;
        } else if (isEditing && initialData?.id) {
          // Construct URL with language in path for editing mode
          const basePath = getBasePath();
          const pointId = initialData.id;
          const language = item.language;
          url = `${basePath}/tour-points/${pointId}/audio/${language}/audio.m4a`;
        }
        // For new creation, leave URL empty - backend will construct it after upload

        return {
          url: url,
          language: item.language
        };
      });

      // Build preview audio URL array from existing entries (mirrors mainAudioUrlArray pattern)
      const previewAudioUrlArray = previewAudios
        .filter(item => item.language)
        .map(item => ({
          url: item.existingUrl || '',
          language: item.language,
        }));
      // Helper function to extract relative path from full URL
      const extractRelativePath = (fullUrl: string): string => {
        if (!fullUrl) return '';
        // If it's already a relative path, return as is
        if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
          return fullUrl;
        }
        // Extract path after the domain
        try {
          const url = new URL(fullUrl);
          // Return path starting from /trippy-tour-apps/...
          const pathParts = url.pathname.split('/trippy-tour-apps/');
          if (pathParts.length > 1) {
            return `/trippy-tour-apps/${pathParts[1]}`;
          }
          // Fallback: return just the pathname
          return url.pathname;
        } catch {
          // If URL parsing fails, try to extract path manually
          const match = fullUrl.match(/\/trippy-tour-apps\/.+$/);
          return match ? match[0] : fullUrl;
        }
      };

      const mainAudioMap = validMainAudios.map(item => {
        let filePath = '';
        if (item.existingUrl) {
          // Extract relative path from full URL
          filePath = extractRelativePath(item.existingUrl);
        } else if (item.file) {
          // For new files, use the filename
          filePath = item.file.name;
        }
        
        return {
          language: item.language,
          file: filePath,
          text: validNameEntries.find(n => n.language === item.language)?.text || '',
        };
      });
      
      const previewAudioMap = previewAudios
        .filter(item => item.language && (item.file || item.existingUrl))
        .map(item => {
          let filePath = '';
          if (item.existingUrl) {
            // Extract relative path from full URL
            filePath = extractRelativePath(item.existingUrl);
          } else if (item.file) {
            // For new files, use the filename
            filePath = item.file.name;
          }
          
          return {
            language: item.language,
            file: filePath,
            text: validNameEntries.find(n => n.language === item.language)?.text || '',
          };
        });

      if (isEditing && initialData?.id) {
        // Upload new audio files first and get their URLs
        const updatedMainAudioUrlArray = [...mainAudioUrlArray];
        const updatedPreviewAudioUrlArray = [...previewAudioUrlArray];

        // Upload new main audio files
        for (const audio of validMainAudios) {
          if (audio.file) {
            try {
              const uploadedUrl = await apiService.uploadTourStopFile(
                audio.file,
                tourId,
                initialData.id,
                'audio',
                audio.language
              );

              // Update the URL in mainAudioUrlArray for this language
              const urlIndex = updatedMainAudioUrlArray.findIndex(
                (item: any) => item.language === audio.language
              );
              if (urlIndex >= 0) {
                updatedMainAudioUrlArray[urlIndex].url = uploadedUrl;
              } else {
                updatedMainAudioUrlArray.push({
                  url: uploadedUrl,
                  language: audio.language
                });
              }

              // Update mainAudioMap with the new URL
              const mapIndex = mainAudioMap.findIndex(
                (item: any) => item.language === audio.language
              );
              if (mapIndex >= 0) {
                mainAudioMap[mapIndex].file = extractRelativePath(uploadedUrl);
              }
            } catch (error) {
              toast.error(`Failed to upload audio for ${audio.language}`);
            }
          }
        }

        // Upload new preview audio files
        for (const audio of previewAudios.filter(item => item.language && item.file)) {
          try {
            const uploadedUrl = await apiService.uploadTourStopFile(
              audio.file!,
              tourId,
              initialData.id,
              'audio',
              audio.language
            );

            // Update updatedPreviewAudioUrlArray with full DO URL (mirrors main audio pattern)
            const urlIndex = updatedPreviewAudioUrlArray.findIndex(
              (item: any) => item.language === audio.language
            );
            if (urlIndex >= 0) {
              updatedPreviewAudioUrlArray[urlIndex].url = uploadedUrl;
            } else {
              updatedPreviewAudioUrlArray.push({ url: uploadedUrl, language: audio.language });
            }

            // Update previewAudioMap with the relative path
            const mapIndex = previewAudioMap.findIndex(
              (item: any) => item.language === audio.language
            );
            if (mapIndex >= 0) {
              previewAudioMap[mapIndex].file = extractRelativePath(uploadedUrl);
            }
          } catch (error) {
            toast.error(`Failed to upload preview audio for ${audio.language}`);
          }
        }
        
        // Upload thumbnail if new one is provided
        if (files.thumbnail) {
          try {
            const thumbnailUrl = await apiService.uploadTourStopFile(
              files.thumbnail,
              tourId,
              initialData.id,
              'thumbnail'
            );
            // Update formData with new thumbnail URL if needed
            setFormData(prev => ({ ...prev, thumbnailUrl }));
          } catch (error) {
            toast.error('Failed to upload thumbnail');
          }
        }
        
        const submitData = new FormData();
        submitData.append('name', JSON.stringify(nameArray));
        submitData.append('discription', JSON.stringify(descriptionArray));
        submitData.append('mainAudioUrl', JSON.stringify(updatedMainAudioUrlArray));
        submitData.append('audioUrl', JSON.stringify(updatedPreviewAudioUrlArray));
        submitData.append('lattitude', formData.lattitude);
        submitData.append('longitude', formData.longitude);
        submitData.append('radius', formData.radius.toString());
        submitData.append('status', formData.status);
        submitData.append('isHidePreview', formData.isHidePreview ? 'true' : 'false');
        submitData.append('isHideStop', formData.isHideStop ? 'true' : 'false');
        submitData.append('stopLanguagesMap', JSON.stringify(mainAudioMap));
        submitData.append('stopLanguagesPreviewMap', JSON.stringify(previewAudioMap));
        
        tourStop = await apiService.updateTourPoint(tourId, initialData.id, submitData);
        toast.success('Tour stop updated successfully');
      } else {
        const formDataToSend = new FormData();
        formDataToSend.append('tourId', tourId.toString());
        formDataToSend.append('name', JSON.stringify(nameArray));
        formDataToSend.append('discription', JSON.stringify(descriptionArray));
        formDataToSend.append('mainAudioUrl', JSON.stringify(mainAudioUrlArray));
        formDataToSend.append('lattitude', formData.lattitude.toString());
        formDataToSend.append('longitude', formData.longitude.toString());
        formDataToSend.append('radius', formData.radius.toString());
        formDataToSend.append('status', formData.status);
        formDataToSend.append('isHidePreview', formData.isHidePreview ? 'true' : 'false');
        formDataToSend.append('isHideStop', formData.isHideStop ? 'true' : 'false');
        formDataToSend.append('stopLanguagesMap', JSON.stringify(mainAudioMap));
        formDataToSend.append('stopLanguagesPreviewMap', JSON.stringify(previewAudioMap));

        if (files.thumbnail) {
          formDataToSend.append('files', files.thumbnail);
        }

        validMainAudios.forEach((audio) => {
          if (audio.file) {
            formDataToSend.append('files', audio.file);
          }
        });

        previewAudios
          .filter(item => item.language && item.file)
          .forEach((audio) => {
            formDataToSend.append('files', audio.file!);
          });

        tourStop = await apiService.createTourStopWithFiles(formDataToSend);
        toast.success('Tour stop created successfully');
      }

      onSuccess?.(tourStop);
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save tour stop');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      discription: '',
      lattitude: '',
      longitude: '',
      radius: 50,
      thumbnailUrl: '',
      mainAudioUrl: '',
      audioUrl: '',
      isHidePreview: false,
      isHideStop: false,
      status: 'active',
      stopLanguagesMap: '[]',
      stopLanguagesPreviewMap: '[]',
    });
    setFiles({});
    setExistingFiles({});
    setValidation(null);
    setShowValidation(false);
    setStopName([{ language: '', text: '' }]);
    setStopDescription([{ language: '', text: '' }]);
    setMainAudios([{ language: '' }]);
    setPreviewAudios([{ language: '' }]);
    setHasAttemptedSubmit(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Edit Tour Stop' : 'Create New Tour Stop'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {isEditing ? 'Update tour stop information' : 'Add a new tour stop with location, audio, and details'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

                 <form onSubmit={handleSubmit} className="p-6 space-y-6">
           {/* Required Fields Note */}
           <div className="text-sm text-gray-600 mb-4">
             <span className="text-red-500">*</span> indicates required fields
           </div>
           
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Tour Stop Information (Multilingual)</h3>
            
            {stopName.map((_, langIndex: number) => {
              const currentLanguage = stopName[langIndex]?.language || 
                                     stopDescription[langIndex]?.language || 
                                     mainAudios[langIndex]?.language ||
                                     previewAudios[langIndex]?.language || "";
              
              const hasLanguage = currentLanguage && currentLanguage.trim() !== '';
              const hasName = stopName[langIndex]?.text && stopName[langIndex]?.text.trim() !== '';
              const hasDescription = stopDescription[langIndex]?.text && stopDescription[langIndex]?.text.trim() !== '';
              const hasMainAudio = mainAudios[langIndex]?.language && (mainAudios[langIndex]?.file || mainAudios[langIndex]?.existingUrl);
              
              const isMissingName = hasAttemptedSubmit && hasLanguage && !hasName;
              const isMissingDescription = hasAttemptedSubmit && hasLanguage && !hasDescription;
              const isMissingMainAudio = hasAttemptedSubmit && hasLanguage && !hasMainAudio;
              const hasAnyMissingField = hasAttemptedSubmit && (isMissingName || isMissingDescription || isMissingMainAudio);
              
              return (
                <div key={langIndex} className={`bg-white border rounded-lg p-4 ${hasAnyMissingField ? 'border-red-300 bg-red-50' : 'border-slate-300'}`}>
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b">
                    <span className="text-sm text-gray-500 font-medium">{langIndex + 1}.</span>
                    <select
                      value={currentLanguage}
                      onChange={(e) => handleSelectLanguage(langIndex, e.target.value)}
                      disabled={true}
                      className="bg-gray-100 border border-slate-300 text-gray-900 text-sm rounded-lg p-2 outline-none min-w-[150px] cursor-not-allowed opacity-75"
                    >
                      <option value="">Select Language</option>
                      {languages
                        ?.filter((lang: any) => {
                          // When editing, show the current language even if not in availableLanguages
                          if (isEditing && currentLanguage && lang.code === currentLanguage) {
                            return true;
                          }
                          // Otherwise, filter by availableLanguages
                          return availableLanguages.length === 0 || availableLanguages.includes(lang.code);
                        })
                        .map((lang: any) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name}
                          </option>
                        ))}
                      {/* If current language is not in the languages list, add it as an option when editing */}
                      {isEditing && currentLanguage && !languages.find((l: any) => l.code === currentLanguage) && (
                        <option value={currentLanguage}>
                          {currentLanguage.toUpperCase()}
                        </option>
                      )}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tour Stop Name *
                      {isMissingName && (
                        <span className="ml-2 text-xs text-red-600 font-semibold">⚠ Name is required for this language</span>
                      )}
                    </label>
                    <input
                      type="text"
                      disabled={!hasLanguage}
                      className={`border rounded-md p-2 w-full text-sm ${
                        !hasLanguage
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-slate-300'
                          : isMissingName 
                            ? 'bg-white border-red-300' 
                            : 'bg-white border-slate-300'
                      }`}
                      value={stopName[langIndex]?.text || ""}
                      onChange={(e) => {
                        const updated = stopName.map((item, i) =>
                          i === langIndex ? { ...item, text: e.target.value } : item
                        );
                        setStopName(updated);
                      }}
                      placeholder={!hasLanguage ? "Please select a language first" : "Enter tour stop name"}
                      maxLength={255}
                    />
                    <div className="text-xs text-gray-500 mt-1 text-right">
                      {stopName[langIndex]?.text?.length || 0}/255 characters
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                      {isMissingDescription && (
                        <span className="ml-2 text-xs text-red-600 font-semibold">⚠ Description is required for this language</span>
                      )}
                    </label>
                    <textarea
                      disabled={!hasLanguage}
                      className={`border rounded-md p-2 w-full text-sm ${
                        !hasLanguage
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-slate-300'
                          : isMissingDescription 
                            ? 'bg-white border-red-300' 
                            : 'bg-white border-slate-300'
                      }`}
                      value={stopDescription[langIndex]?.text || ""}
                      onChange={(e) => {
                        const updated = stopDescription.map((item, i) =>
                          i === langIndex ? { ...item, text: e.target.value } : item
                        );
                        setStopDescription(updated);
                      }}
                      placeholder={!hasLanguage ? "Please select a language first" : "Enter tour stop description"}
                      rows={3}
                      maxLength={2047}
                    />
                    <div className="text-xs text-gray-500 mt-1 text-right">
                      {stopDescription[langIndex]?.text?.length || 0}/2047 characters
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Main Audio * 
                      {isMissingMainAudio && (
                        <span className="ml-2 text-xs text-red-600 font-semibold">⚠ Main audio is required for this language</span>
                      )}
                    </label>
                    <input
                      type="file"
                      accept="audio/*"
                      disabled={!hasLanguage}
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        handleAudioFileChange(langIndex, 'main', file);
                      }}
                      className={`block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold ${
                        !hasLanguage
                          ? 'file:bg-gray-400 file:text-gray-200 file:cursor-not-allowed opacity-50'
                          : 'file:bg-green-600 file:text-white hover:file:bg-green-700 file:cursor-pointer'
                      } ${isMissingMainAudio ? 'border-red-300 border-2' : ''}`}
                    />
                    {mainAudios[langIndex]?.file && (
                      <div className="mt-2 flex items-center space-x-2 text-sm text-gray-600">
                        <span>✓ {mainAudios[langIndex].file?.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAudioFileClick(langIndex, 'main')}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {mainAudios[langIndex]?.existingUrl && !mainAudios[langIndex]?.file && (
                      <div className="mt-2 flex items-center space-x-2 text-sm text-gray-600">
                        <span>✓ {mainAudios[langIndex].existingName}</span>
                        <span className="text-green-600">(Existing file)</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAudioFileClick(langIndex, 'main')}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preview Audio <span className="text-xs text-gray-500 font-normal">(Optional)</span>
                    </label>
                    <input
                      type="file"
                      accept="audio/*"
                      disabled={!hasLanguage}
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        handleAudioFileChange(langIndex, 'preview', file);
                      }}
                      className={`block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold ${
                        !hasLanguage
                          ? 'file:bg-gray-400 file:text-gray-200 file:cursor-not-allowed opacity-50'
                          : 'file:bg-purple-600 file:text-white hover:file:bg-purple-700 file:cursor-pointer'
                      }`}
                    />
                    {previewAudios[langIndex]?.file && (
                      <div className="mt-2 flex items-center space-x-2 text-sm text-gray-600">
                        <span>✓ {previewAudios[langIndex].file?.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAudioFileClick(langIndex, 'preview')}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {previewAudios[langIndex]?.existingUrl && !previewAudios[langIndex]?.file && (
                      <div className="mt-2 flex items-center space-x-2 text-sm text-gray-600">
                        <span>✓ {previewAudios[langIndex].existingName}</span>
                        <span className="text-purple-600">(Existing file)</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAudioFileClick(langIndex, 'preview')}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="draft">Draft</option>
            </select>
          </div>

          {/* Location Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-primary" />
              Location Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                               <label className="block text-sm font-medium text-gray-700 mb-2">
                 Latitude <span className="text-red-500">*</span>
               </label>
                                                 <input
                  type="text"
                  name="lattitude"
                  value={formData.lattitude}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., 40.7128"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Enter latitude (e.g., 40.7128 or -40.7128)
                </div>
              </div>

              <div>
                               <label className="block text-sm font-medium text-gray-700 mb-2">
                 Longitude <span className="text-red-500">*</span>
               </label>
                                                 <input
                  type="text"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., -74.0060"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Enter longitude (e.g., -74.0060 or 74.0060)
                </div>
              </div>

              <div>
                                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   Radius (meters) <span className="text-red-500">*</span>
                 </label>
                                                   <input
                    type="number"
                    name="radius"
                    value={formData.radius}
                    onChange={handleInputChange}
                    min="1"
                    max="10000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="50"
                  />
                 <div className="text-xs text-gray-500 mt-1">
                   Range: 1-10,000 meters
                 </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Image className="h-5 w-5 mr-2 text-blue-600" />
              Thumbnail Image
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Upload thumbnail image. Supported formats: Images (JPG, PNG, GIF)
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center">
                <div className="w-3 h-3 bg-blue-600 rounded-full mr-2"></div>
                Thumbnail Image <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="space-y-2">
                <input
                  type="file"
                  name="thumbnail"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer"
                />
                {existingFiles.thumbnail && !files.thumbnail && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <span>✓ {existingFiles.thumbnail.name}</span>
                      <span className="text-blue-600">(Existing file)</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveExistingFileClick('thumbnail')}
                        className="text-red-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="relative w-24 h-24 border border-gray-300 rounded-lg overflow-hidden">
                      <img
                        src={existingFiles.thumbnail.url}
                        alt="Existing thumbnail"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="hidden w-full h-full bg-gray-200 flex items-center justify-center">
                        <Image className="h-8 w-8 text-gray-400" />
                      </div>
                    </div>
                  </div>
                )}

                {files.thumbnail && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <span>✓ {files.thumbnail.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFileClick('thumbnail')}
                        className="text-red-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="relative w-24 h-24 border border-gray-300 rounded-lg overflow-hidden">
                      <img
                        src={URL.createObjectURL(files.thumbnail)}
                        alt="New thumbnail preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Visibility Settings */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Visibility Settings</h3>
            
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="isHidePreview"
                  checked={formData.isHidePreview}
                  onChange={handleInputChange}
                  disabled={formData.isHideStop}
                  className={`h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded ${
                    formData.isHideStop ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                />
                <span className={`ml-2 text-sm ${formData.isHideStop ? 'text-gray-500' : 'text-gray-700'}`}>
                  Hide from preview
                  {formData.isHideStop && (
                    <span className="ml-1 text-xs text-gray-400">(auto-selected)</span>
                  )}
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="isHideStop"
                  checked={formData.isHideStop}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Hide from tour stops</span>
              </label>
            </div>
          </div>

          {/* Validation Results */}
          {showValidation && validation && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Info className="h-5 w-5 mr-2 text-primary" />
                Validation Results
              </h3>
              
              {validation.errors.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    Errors ({validation.errors.length})
                  </h4>
                  <ul className="list-disc list-inside text-sm text-red-600 space-y-1">
                    {validation.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {validation.warnings.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-yellow-700 mb-2 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    Warnings ({validation.warnings.length})
                  </h4>
                  <ul className="list-disc list-inside text-sm text-yellow-600 space-y-1">
                    {validation.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {validation.isValid && validation.warnings.length === 0 && (
                <div className="text-green-600 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  All validations passed successfully!
                </div>
              )}
            </div>
          )}




          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <div className="flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                disabled={isLoading}
              >
                Reset Form
              </Button>
              
              <Button
                type="button"
                variant="outline"
                onClick={validateForm}
                disabled={isLoading}
              >
                Validate Data
              </Button>
            </div>

            <div className="flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="min-w-[120px]"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  isEditing ? 'Update Stop' : 'Create Stop'
                )}
              </Button>
            </div>
          </div>
        </form>

        {/* Confirmation Dialog */}
        {confirmationDialog.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Confirm Delete
              </h3>
              <p className="text-sm text-gray-700 mb-6">
                {confirmationDialog.message}
              </p>
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={confirmationDialog.onConfirm}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TourStopCreationForm;
